/**
 * RequestorsTab — the people who ask for loads (OPEN-053, ClickUp 86bc5hjn1).
 *
 * Fifth tab under Sites & Operators, alongside the other tenant catalogs it behaves like.
 * Reads are open to every dispatcher role (VIEW_HAULER_CATALOG) because ticket entry needs
 * the names; `canManage` (MANAGE_HAULER_CATALOG — Dispatch Manager and up) only hides the
 * add/edit affordances. The server is the authoritative gate.
 *
 * Deactivated people stay listed, dimmed, because the only way back is to reactivate them —
 * there is no delete (both FKs are ON DELETE RESTRICT).
 *
 * Clicking a row opens the DETAIL pane (everywhere that person is assigned), not the edit
 * modal — "which sites is Jane on" is the question you have before you edit anything, and
 * editing is one click further in. Built for hundreds of people: the list is filtered and
 * render-capped rather than dumped in full.
 */
import * as React from 'react';
import { useRequestors, type Requestor } from './hooks';
import { RequestorFormModal } from './RequestorFormModal';
import { BulkAssignModal } from './BulkAssignModal';
import { RequestorDetail } from './RequestorDetail';
import { capForRender, capNotice } from './list-scale';
import { useUrlDetail, useUrlParam } from '../../lib/url-state';
import {
  ActiveBadge,
  AddButton,
  Pill,
  SectionHeader,
  rowStyle,
  searchInputStyle,
  subText,
} from '../sites-operators/ui-bits';
import { formatNumber } from '@lodex-pro/shared/number-format';

export function RequestorsTab({ canManage }: { canManage: boolean }): React.JSX.Element {
  const { data, isLoading, isError } = useRequestors();
  // OPEN-191 — the filter and the open person live in the URL (`?requestor=<id>`), so browser
  // Back closes the detail and a refresh reopens it. An id not in the list shows the list.
  const [filter, setFilter] = useUrlParam('requestorsQ', '', []);
  const [formOpen, setFormOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<Requestor | null>(null);
  const [bulkOpen, setBulkOpen] = React.useState(false);
  const [selectedId, setSelectedId] = useUrlDetail('requestor');

  const requestors = data?.requestors ?? [];
  const needle = filter.trim().toLowerCase();
  const matching = needle
    ? requestors.filter(
        (r) =>
          r.name.toLowerCase().includes(needle) ||
          (r.company ?? '').toLowerCase().includes(needle) ||
          (r.phone ?? '').toLowerCase().includes(needle) ||
          (r.email ?? '').toLowerCase().includes(needle),
      )
    : requestors;
  const capped = capForRender(matching);
  const notice = capNotice(capped, 'requestors');

  // Re-read the selected row from the live list so an edit (or a bulk assign) is reflected
  // in the open detail pane without closing it.
  const selected =
    selectedId === null ? null : (requestors.find((r) => r.id === selectedId) ?? null);

  if (selected !== null) {
    return (
      <>
        <RequestorDetail
          requestor={selected}
          canManage={canManage}
          onEdit={(): void => {
            setEditing(selected);
            setFormOpen(true);
          }}
          onClose={(): void => setSelectedId(null)}
        />
        <RequestorFormModal open={formOpen} onOpenChange={setFormOpen} editing={editing} />
      </>
    );
  }

  return (
    <div>
      <SectionHeader
        title="Requestors"
        count={requestors.length}
        action={
          canManage ? (
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              {/* The bulk editor is the data-entry path, not a power-user extra: the table
                  ships empty, so a customer's forty locations get populated here. */}
              <button
                type="button"
                onClick={(): void => setBulkOpen(true)}
                style={{
                  fontSize: '13px',
                  color: 'var(--color-accent-blue)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                }}
              >
                Assign to locations…
              </button>
              <AddButton
                label="Add Requestor"
                onClick={(): void => {
                  setEditing(null);
                  setFormOpen(true);
                }}
              />
            </div>
          ) : null
        }
      />
      <p style={{ ...subText, marginBottom: '10px' }}>
        The people who call in loads. Assign up to two per site or well; dispatchers pick one of
        those two when they create a ticket.
      </p>
      <input
        value={filter}
        onChange={(e): void => setFilter(e.target.value)}
        placeholder="Filter requestors…"
        style={searchInputStyle}
        aria-label="Filter requestors"
      />

      {isLoading && <p style={subText}>Loading…</p>}
      {isError && (
        <p style={{ ...subText, color: 'var(--color-danger)' }}>Could not load requestors.</p>
      )}
      {!isLoading && !isError && requestors.length === 0 && (
        <p style={subText}>
          No requestors yet. Ticket entry keeps using the free-text requester fields until you add
          some.
        </p>
      )}
      {!isLoading && requestors.length > 0 && matching.length === 0 && (
        <p style={subText}>No requestors match that filter.</p>
      )}

      <div>
        {capped.shown.map((r) => (
          <div
            key={r.id}
            style={{
              ...rowStyle,
              cursor: 'pointer',
              opacity: r.isActive ? 1 : 0.55,
            }}
            onClick={(): void => setSelectedId(r.id)}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontWeight: 600 }}>{r.name}</span>
                {r.company !== null && <Pill label={r.company} tone="accent" />}
              </div>
              <div style={subText}>
                {[r.phone, r.email].filter((v) => v !== null).join(' · ') || 'No contact details'}
              </div>
            </div>
            <span style={subText}>
              {r.assignmentCount === 0
                ? 'Unassigned'
                : `${formatNumber(r.assignmentCount)} ${r.assignmentCount === 1 ? 'site' : 'sites'}`}
            </span>
            <ActiveBadge active={r.isActive} />
          </div>
        ))}
      </div>

      {notice !== null && <p style={{ ...subText, marginTop: '8px' }}>{notice}</p>}

      <RequestorFormModal open={formOpen} onOpenChange={setFormOpen} editing={editing} />
      <BulkAssignModal open={bulkOpen} onOpenChange={setBulkOpen} />
    </div>
  );
}
